import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { HorizontalScroll as HorizontalScrollMemoized } from '../HorizontalScroll/HorizontalScroll.js';
import { useTabsKeyboardNavigation, getTabId, getPanelId } from './useTabsKeyboardNavigation.js';
import { Tab as TabMemoized } from './TabItem.js';

var _excluded = ["id", "label", "divider", "items", "selection", "size", "width", "children", "onTabChange", "previousTabsLabel", "nextTabsLabel", "behavior"];
var LINE_CLAMP_ONE = 1;
var LINE_CLAMP_TWO = 2;

/**
 * Tabs help users navigate through different sections of related content, without leaving the page.
 */
var Tabs = function Tabs(_ref) {
  var id = _ref.id,
    tabsLabel = _ref.label,
    divider = _ref.divider,
    items = _ref.items,
    selection = _ref.selection,
    size = _ref.size,
    width = _ref.width,
    children = _ref.children,
    onTabChange = _ref.onTabChange,
    previousTabsLabel = _ref.previousTabsLabel,
    nextTabsLabel = _ref.nextTabsLabel,
    behavior = _ref.behavior,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _React$useState = React.useState(selection || 0),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    active = _React$useState2[0],
    setActive = _React$useState2[1];
  var focusedTabIndex = React.useRef(0);
  var scrollRef = React.useRef(null);
  var scrollable = width === 'content-fit';
  var lineClamp = scrollable ? LINE_CLAMP_ONE : LINE_CLAMP_TWO;
  React.useEffect(function () {
    return setActive(selection || 0);
  }, [selection]);
  var changeTab = React.useCallback(function (index) {
    setActive(index);
    focusedTabIndex.current = index;
    onTabChange === null || onTabChange === void 0 || onTabChange(index);
  }, [onTabChange]);
  var handleKeyDownA11yFix = useTabsKeyboardNavigation(items.length, id, scrollRef, active, changeTab);
  var tabs = React.useMemo(function () {
    return items.map(function (item, index) {
      var isString = typeof item === 'string';
      var tabProps = !isString ? item.props : {
        label: item
      };
      var isActive = index === active;
      var flex = !scrollable ? 1 : undefined;
      var onClick = function onClick() {
        return changeTab(index);
      };
      var key = getTabId(id, index);
      var ariaControls = getPanelId(id, index);
      var keyEventHandler = function keyEventHandler(e) {
        return handleKeyDownA11yFix(e, index);
      };
      return /*#__PURE__*/React.createElement(TabMemoized, _extends({}, tabProps, {
        id: key,
        "aria-controls": ariaControls,
        key: key,
        onClick: onClick,
        onKeyDown: keyEventHandler,
        isActive: isActive,
        size: size,
        lineClamp: lineClamp,
        flex: flex,
        width: width
      }));
    });
  }, [active, changeTab, id, items, lineClamp, scrollable, size, width, handleKeyDownA11yFix]);
  var childrenWithA11YProps = React.useMemo(function () {
    return React.Children.map(children, function (child, index) {
      if (/*#__PURE__*/React.isValidElement(child)) {
        var ariaLabelledBy = getTabId(id, index);
        var ariaControlsId = getPanelId(id, index);
        return /*#__PURE__*/React.cloneElement(child, {
          id: ariaControlsId,
          'aria-labelledby': ariaLabelledBy,
          role: 'tabpanel'
        });
      }
      return child;
    });
  }, [children, id]);
  var ariaOwns = React.useMemo(function () {
    return items === null || items === void 0 ? void 0 : items.map(function (_item, index) {
      return getTabId(id, index);
    }).join(' ');
  }, [id, items]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(HorizontalScrollMemoized, _extends({}, rest, {
    ref: scrollRef,
    role: "tablist",
    behavior: behavior,
    "aria-label": tabsLabel,
    whiteSpace: scrollable ? 'nowrap' : undefined,
    borderWidth: divider ? ['0', '0', '1'] : '0',
    borderColor: "base.default",
    borderStyle: "solid",
    previousLabel: previousTabsLabel,
    nextLabel: nextTabsLabel,
    active: active,
    onActiveChange: changeTab,
    "aria-owns": ariaOwns,
    restrictFocus: false
  }), tabs), childrenWithA11YProps);
};
Tabs.defaultProps = {
  divider: true,
  selection: 0,
  size: 's',
  width: 'parent-fit',
  behavior: 'item'
};
var TabsMemoized = /*#__PURE__*/React.memo(Tabs);
TabsMemoized.displayName = 'Tabs';

export { Tabs as BaseTabs, TabsMemoized as Tabs };
//# sourceMappingURL=Tabs.js.map
